import { Helmet } from 'react-helmet-async';

const SITE_ORIGIN = 'https://techieinteriors.co.ke';
const SITE_NAME = 'Techie Interiors';
const DEFAULT_TITLE = 'Techie Interiors | Interior Design Services in Nairobi, Kenya';
const DEFAULT_DESCRIPTION =
  'Techie Interiors provides residential and commercial interior solutions in Nairobi and across Kenya, including gypsum ceilings, TV wall units, kitchen cabinets, wardrobes, and fit-outs.';
const DEFAULT_IMAGE = `${SITE_ORIGIN}/techLogo.jpeg`;
const DEFAULT_IMAGE_ALT = 'Techie Interiors interior design project showcase';

const normalizeText = (value) => (value ? value.replace(/\s+/g, ' ').trim() : '');

const toAbsoluteUrl = (value, fallback) => {
  let url = value || fallback;

  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `${SITE_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
  }

  // Enforce root domain trailing slash, but remove trailing slashes for sub-paths
  // This ensures exact match with sitemap.xml to prevent canonical duplicates
  if (url === SITE_ORIGIN) {
    return `${SITE_ORIGIN}/`;
  } else if (url.length > SITE_ORIGIN.length + 1 && url.endsWith('/')) {
    return url.slice(0, -1);
  }

  return url;
};

const SEO = ({
  title,
  description,
  image,
  imageAlt = DEFAULT_IMAGE_ALT,
  url,
  type = 'website',
  structuredData = null,
  noindex = false,
}) => {
  const normalizedUrl = toAbsoluteUrl(url, SITE_ORIGIN);
  const normalizedImage = toAbsoluteUrl(image, DEFAULT_IMAGE);
  const normalizedTitle = normalizeText(title) || DEFAULT_TITLE;
  const normalizedDescription = normalizeText(description) || DEFAULT_DESCRIPTION;
  const robotsContent = noindex
    ? 'noindex, nofollow, noarchive'
    : 'index, follow, max-image-preview:large';

  const structuredDataArray = Array.isArray(structuredData)
    ? structuredData.filter(Boolean)
    : structuredData
      ? [structuredData]
      : [];

  const generateBreadcrumb = (currentUrl) => {
    if (!currentUrl || currentUrl === SITE_ORIGIN || currentUrl === `${SITE_ORIGIN}/`) return null;
    try {
      const urlObj = new URL(currentUrl);
      const paths = urlObj.pathname.split('/').filter(p => p);
      if (paths.length === 0) return null;

      const itemListElement = [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": SITE_ORIGIN
        }
      ];

      let accumulatedPath = SITE_ORIGIN;
      paths.forEach((pathSegment, index) => {
        accumulatedPath += `/${pathSegment}`;
        const name = pathSegment.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        itemListElement.push({
          "@type": "ListItem",
          "position": index + 2,
          "name": name,
          "item": accumulatedPath
        });
      });

      return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": itemListElement
      };
    } catch (e) {
      return null;
    }
  };

  const breadcrumbSchema = generateBreadcrumb(normalizedUrl);
  if (breadcrumbSchema) {
    structuredDataArray.push(breadcrumbSchema);
  }

  return (
    <Helmet prioritizeSeoTags>
      <title>{normalizedTitle}</title>
      <meta name="description" content={normalizedDescription} />
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <meta name="author" content={SITE_NAME} />
      <meta name="language" content="en-KE" />
      <meta name="geo.region" content="KE-30" />
      <meta name="geo.placename" content="Nairobi" />
      <meta name="ICBM" content="-1.286389,36.817223" />
      <link rel="canonical" href={normalizedUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:url" content={normalizedUrl} />
      <meta property="og:title" content={normalizedTitle} />
      <meta property="og:description" content={normalizedDescription} />
      <meta property="og:image" content={normalizedImage} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_KE" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={normalizedUrl} />
      <meta name="twitter:title" content={normalizedTitle} />
      <meta name="twitter:description" content={normalizedDescription} />
      <meta name="twitter:image" content={normalizedImage} />
      <meta name="twitter:image:alt" content={imageAlt} />
      <meta name="twitter:site" content="@techieinteriors" />
      <meta name="twitter:creator" content="@techieinteriors" />

      {structuredDataArray.map((item, index) => (
        <script key={`schema-${index}`} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(item) }} />
      ))}
    </Helmet>
  );
};

export default SEO;
